type AccessDecision = { type: 'continue' } | { type: 'redirect'; path: string }

interface AccessDecisionInput {
  organizationSlug?: string
  workspaceId?: string
  isUserInWorkspace: boolean
  isOrganizationMember: boolean
}

/**
 * Decide the access verdict for an organization/workspace route. Pure — no
 * Nuxt/store side effects; the middleware (~/middleware/05.access.global.ts)
 * resolves membership and owns the refresh-retry, then calls this.
 *
 * Access requires membership for EVERYONE, super-users included — being a super
 * is no longer a blanket bypass at either level:
 *  - Workspace page: the super must join the workspace via
 *    `system admin > workspaces > join workspace`.
 *  - Organization page: the super must join the org via
 *    `system admin > organisations > join organisation`.
 * Both join actions create the membership row the gate checks. (The
 * system-admin section itself stays super-only via 06.user-role, so supers can
 * always reach the list to join from.)
 */
export function decideAccessAction(input: AccessDecisionInput): AccessDecision {
  const { organizationSlug, workspaceId } = input

  if (!organizationSlug) return { type: 'continue' }

  // Workspace page — membership in the workspace is the authoritative check
  // (it implies membership in the owning organization).
  if (workspaceId) {
    return input.isUserInWorkspace
      ? { type: 'continue' }
      : { type: 'redirect', path: '/403' }
  }

  // Organization-level page — the user must belong to the organization.
  return input.isOrganizationMember
    ? { type: 'continue' }
    : { type: 'redirect', path: '/403' }
}
